import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, AlertCircle } from "lucide-react";
import api, { getUser, errMsg, fieldErrors } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

function FieldError({ msg, testid }) {
    if (!msg) return null;
    return (
        <div className="mt-1.5 flex items-start gap-1 text-xs text-rose-600"
            data-testid={testid}>
            <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
            <span>{msg}</span>
        </div>
    );
}

/**
 * Complete-profile step for doctors who signed up via Google.
 * Collects the mandatory business fields the classic signup form asks for:
 * medical registration number, specialization, mobile, city, state, country,
 * plus explicit terms acceptance. Only routed to for new Google users
 * (profile_incomplete=true from /auth/google/session).
 */
export default function CompleteProfile() {
    const nav = useNavigate();
    const [busy, setBusy] = useState(false);
    const [errors, setErrors] = useState({});
    const user = getUser();
    const [form, setForm] = useState({
        medical_registration_number: "",
        specialization: "",
        mobile: "",
        city: "",
        state: "",
        country: "India",
        terms_accepted: false,
    });

    useEffect(() => {
        // If they somehow landed here without a JWT, bounce them to login.
        if (!user) nav("/login", { replace: true });
    }, [user, nav]);

    const set = (k) => (e) => {
        setForm({ ...form, [k]: e.target ? e.target.value : e });
        // Clear this field's error as soon as the user edits it.
        if (errors[k]) setErrors((prev) => { const n = { ...prev }; delete n[k]; return n; });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.terms_accepted) {
            toast.error("Please accept the Terms & Conditions to continue.");
            return;
        }
        setBusy(true);
        setErrors({});
        try {
            await api.post("/auth/google/complete-profile", form);
            toast.success("Profile completed — welcome to Doctor Digital Identity!");
            nav("/doctor", { replace: true });
        } catch (err) {
            const fe = fieldErrors(err);
            if (Object.keys(fe).length) {
                setErrors(fe);
                toast.error("Please check the highlighted fields.");
            } else {
                toast.error(errMsg(err, "Failed to save profile"));
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
            <div className="bg-[#123B5D] text-white hidden lg:flex flex-col justify-between p-12">
                <Link to="/" className="inline-flex items-center gap-2.5 text-lg font-bold">
                    <img src="/brand-icon.png" alt="Doctor Digital ID" className="h-10 w-10 object-contain" />
                    <span>DOCTOR <span className="text-[#60A5FA]">DIGITAL ID</span></span>
                </Link>
                <div className="max-w-md">
                    <h1 className="text-4xl font-bold leading-tight">
                        Almost there, {user?.full_name?.split(" ")[0] || "Doctor"}.
                    </h1>
                    <p className="mt-4 text-white/70">
                        We need a few practice details to set up your verified digital
                        identity. This information appears on your public profile only
                        after admin review — nothing goes live until you approve it.
                    </p>
                </div>
                <div className="text-xs text-white/50">
                    Signed in as {user?.email} via Google.
                </div>
            </div>
            <div className="flex items-center justify-center p-6 md:p-12 bg-[#F5F8FA]">
                <form
                    onSubmit={submit}
                    className="ddi-card p-8 w-full max-w-xl"
                    data-testid="complete-profile-form"
                >
                    <div className="inline-flex items-center justify-center h-12 w-12 rounded-lg bg-[#0F766E]/10 text-[#0F766E]">
                        <UserPlus size={20} />
                    </div>
                    <h2 className="mt-4 text-2xl font-bold text-[#123B5D]">
                        Complete your doctor profile
                    </h2>
                    <p className="text-sm text-[#17212B]/60 mt-1">
                        Signed in as <span className="font-semibold">{user?.email}</span>.
                    </p>

                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <Label htmlFor="reg">Medical registration number</Label>
                            <Input
                                id="reg"
                                value={form.medical_registration_number}
                                onChange={set("medical_registration_number")}
                                placeholder="KA-12345"
                                data-testid="cp-reg"
                                aria-invalid={!!errors.medical_registration_number}
                                required
                            />
                            <FieldError msg={errors.medical_registration_number} testid="cp-reg-error" />
                        </div>
                        <div>
                            <Label htmlFor="spec">Primary specialization</Label>
                            <Input
                                id="spec"
                                value={form.specialization}
                                onChange={set("specialization")}
                                placeholder="Cardiology"
                                data-testid="cp-spec"
                                aria-invalid={!!errors.specialization}
                                required
                            />
                            <FieldError msg={errors.specialization} testid="cp-spec-error" />
                        </div>
                        <div>
                            <Label htmlFor="mobile">Mobile</Label>
                            <Input
                                id="mobile"
                                value={form.mobile}
                                onChange={set("mobile")}
                                placeholder="+91 9XXXXXXXXX"
                                data-testid="cp-mobile"
                                aria-invalid={!!errors.mobile}
                                required
                            />
                            <FieldError msg={errors.mobile} testid="cp-mobile-error" />
                        </div>
                        <div>
                            <Label htmlFor="city">City</Label>
                            <Input
                                id="city"
                                value={form.city}
                                onChange={set("city")}
                                data-testid="cp-city"
                                aria-invalid={!!errors.city}
                                required
                            />
                            <FieldError msg={errors.city} testid="cp-city-error" />
                        </div>
                        <div>
                            <Label htmlFor="state">State</Label>
                            <Input
                                id="state"
                                value={form.state}
                                onChange={set("state")}
                                data-testid="cp-state"
                                aria-invalid={!!errors.state}
                                required
                            />
                            <FieldError msg={errors.state} testid="cp-state-error" />
                        </div>
                        <div className="md:col-span-2">
                            <Label htmlFor="country">Country</Label>
                            <Input
                                id="country"
                                value={form.country}
                                onChange={set("country")}
                                data-testid="cp-country"
                                aria-invalid={!!errors.country}
                                required
                            />
                            <FieldError msg={errors.country} testid="cp-country-error" />
                        </div>
                    </div>

                    <label className="mt-5 flex items-start gap-2 text-sm text-[#17212B]/80">
                        <Checkbox
                            checked={form.terms_accepted}
                            onCheckedChange={(v) => setForm({ ...form, terms_accepted: !!v })}
                            data-testid="cp-terms"
                            className="mt-1"
                        />
                        <span>
                            I accept the{" "}
                            <a href="/terms" target="_blank" rel="noreferrer" className="text-[#0F766E] font-semibold hover:underline">
                                Terms &amp; Conditions
                            </a>{" "}
                            and{" "}
                            <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#0F766E] font-semibold hover:underline">
                                Privacy Policy
                            </a>
                            . I understand this is a professional identity platform, not a diagnostic service.
                        </span>
                    </label>

                    <button
                        type="submit"
                        className="mt-6 w-full ddi-teal-btn disabled:opacity-60"
                        disabled={busy}
                        data-testid="cp-submit"
                    >
                        {busy ? "Saving…" : "Save and continue"}
                    </button>
                </form>
            </div>
        </div>
    );
}
